import createReducer from 'redux/createReducer';
import update from 'immutability-helper';

import linkTypes from 'constants/link-types';

import { FAVOURITES_SORT_API_TO_URL_MAP } from 'redux/modules/favourites-experience/maps';
import { SORT_URL_CATEGORY_RANKING } from 'redux/modules/favourites-experience/constants';

import { isBoughtInStoreSuccessfullyLoaded } from 'redux/modules/bought-in-store/selectors';

import type {
  FavouritesExperienceRequestState,
  FavouritesExperienceSuccessState,
  FavouritesExperienceState,
  FavouritesSortByAction,
  TrimmedCategory,
  FavouritesExperienceSuccessAction,
  FavouritesExperienceRequestAction,
  FavouritesExperienceFetcherResult,
} from 'redux/modules/favourites-experience/types';

import { GET_BOUGHT_IN_STORE_PRODUCTS, BOUGHT_IN_STORE_SET_SORT_ORDER } from '../actions/types';

import { initialState } from '../state';

const getBoughtInStoreRequest = (
  state: FavouritesExperienceState,
  { onOffer, sortBy, start, categoryId }: FavouritesExperienceRequestAction,
): FavouritesExperienceRequestState | FavouritesExperienceSuccessState => ({
  ...state,
  offersFilter: !!onOffer,
  sortBy,
  categoryIdFilter: categoryId,
  loading: !start,
  loadingMore: !!start,
});

const getCategories = (
  state: FavouritesExperienceState,
  {
    categories,
    isLoadMore,
  }: { categories: FavouritesExperienceFetcherResult['categories']; isLoadMore: boolean },
) => {
  if (!categories) {
    return [];
  }

  const trimmedCategories: TrimmedCategory[] = categories.map(
    ({ name, id, orderedComponentsAndProducts }) => ({
      name,
      id,
      orderedComponentsAndProducts,
    }),
  );

  if (!isLoadMore) {
    return trimmedCategories;
  }

  const categoriesState = (state as FavouritesExperienceSuccessState).categories;
  const lastCategoryInState = categoriesState[categoriesState.length - 1];
  const firstCategoryInTrimmed = trimmedCategories[0];

  if (lastCategoryInState.id === firstCategoryInTrimmed.id) {
    trimmedCategories[0].orderedComponentsAndProducts = [
      ...lastCategoryInState.orderedComponentsAndProducts,
      ...firstCategoryInTrimmed.orderedComponentsAndProducts,
    ];
  }

  return update(categoriesState, { $push: trimmedCategories });
};

const getBoughtInStoreSuccess = (
  state: FavouritesExperienceState,
  {
    result: {
      categories: rawCategories,
      componentsAndProducts,
      filterCategories,
      recommendations,
      criteria,
      ignoreCategories,
      metadata,
    },
    onOffer,
    start,
    categoryId,
  }: FavouritesExperienceSuccessAction,
): FavouritesExperienceSuccessState => {
  const isLoadMore = !!start && isBoughtInStoreSuccessfullyLoaded(state);
  const categories = getCategories(state, { categories: rawCategories, isLoadMore });
  const offersFilterApplied = criteria?.filters.find(filter => filter.group === linkTypes.OFFER)
    ?.filters[0].applied;
  const offersFilterAppliedFallback = onOffer === 1 ? true : null;
  const offersFilter = offersFilterApplied ?? offersFilterAppliedFallback;
  const sortByValue = metadata?.sortBy as keyof typeof FAVOURITES_SORT_API_TO_URL_MAP;
  const sortBy = FAVOURITES_SORT_API_TO_URL_MAP[sortByValue] ?? SORT_URL_CATEGORY_RANKING;

  return {
    loading: false,
    loadingMore: false,
    categories,
    componentsAndProducts: isLoadMore
      ? update(state.componentsAndProducts, { $push: componentsAndProducts })
      : componentsAndProducts,
    categoryIdFilter: categoryId,
    rootCategories: filterCategories,
    ignoreCategories,
    recommendations,
    hasNextPage: metadata.hasNextPage,
    totalResults: metadata.totalResults,
    offersFilter,
    sortBy,
  };
};

const setBoughtInStoreSortBy = (
  state: FavouritesExperienceState,
  { payload: { sortBy } }: FavouritesSortByAction,
) => ({
  ...state,
  sortBy,
});

const actionTypeReducerMap = [
  [GET_BOUGHT_IN_STORE_PRODUCTS.request, getBoughtInStoreRequest],
  [GET_BOUGHT_IN_STORE_PRODUCTS.success, getBoughtInStoreSuccess],
  [BOUGHT_IN_STORE_SET_SORT_ORDER, setBoughtInStoreSortBy],
] as const;

export type PayloadMap = ReducerMapTupleToRecord<typeof actionTypeReducerMap>;
export default createReducer(initialState, actionTypeReducerMap);
